// @ts-strict-ignore This file is in the process of being migrated to Typescript, but we have to go about it iteratively
// Please be cautious and fix typing issues in the code you update here. Type-compliant code should be moved to a separate module.
// -----------------------------------------------------------------------------------------------------------------------------
// ⚠️ DO NOT ADD CODE TO THIS MODULE. IT IS NOT TYPECHECKED AND IS MARKED AS LEGACY BECAUSE WE ARE IN THE PROCESS OF DELETING IT.
// -----------------------------------------------------------------------------------------------------------------------------
import { Linking } from 'react-native'
import DeviceInfo from 'APP/Helpers/DeviceInfo'
import { extractFileIds } from 'APP/Helpers/Intake'
import Toast from 'react-native-toast-message'
import { all, call, fork, select, spawn } from 'typed-redux-saga'
import { put, take, race, cancel, delay } from 'redux-saga/effects'
import omit from 'lodash/omit'
import { camelizeKeys } from 'humps'

import { navigationRef as Nav, CONVERSATION_ROUTES } from 'APP/Nav'
import * as NestedNavHelper from 'APP/Nav/NestedNavHelper'

// Actions and Types
import PatientHistoryActions, {
  buildInitialFilePayload,
  PatientHistoryTypes,
} from 'APP/Redux/PatientHistoryRedux'
import PatientActions, {
  selectPreferredLanguage,
  selectPatientRegion,
  selectPatientProfile,
  selectPatientState,
} from 'APP/Redux/PatientRedux'
import {
  LoginTypes,
  selectAccessToken,
  selectLoginState,
  selectMattermostTeamId,
} from 'APP/Redux/LoginRedux'
import { AppSessionTypes } from 'APP/Redux/AppSessionRedux'
import { updateAppointment } from 'APP/Store/ProviderBooking/sagas'
import { providerBookingActions } from 'APP/Store/ProviderBooking/slice'

// Services
import { EmergencyRoom } from '@dialogue/services'
import { getBio } from 'APP/Services/CMS'
import Mattermost, { type MattermostClient } from 'APP/Services/Mattermost'
import Usher from 'APP/Services/Usher'
import { normalizeAppointments } from 'APP/Lib/AppointmentHelpers'
import { addCommandSlots, isValidPostData } from 'APP/Lib/MessagingHelpers'
import { canRedirectToConversation } from 'APP/Lib/MessagingHelpers'
import {
  fetchNewPostsRequestHelper,
  isSelfPost,
  isEncounterPost,
  isChannelInconsistent,
} from 'APP/Lib/MessagingHelpers'
import Config from 'APP/Config'
import { checkLocationService } from 'APP/NativeModules/PermissionsService'
import { resolveExtraProps } from 'APP/Lib/MessagingHelpers'
import i18n from 'APP/I18n'
import CoreData from 'APP/Services/Coredata'
import sprigClient from 'APP/Services/Sprig'
import { showToast } from 'APP/Converse/Toast/helpers'
import Silkroad, { SilkroadClient } from 'APP/Services/Silkroad'

// Sagas
import { setIsConnectedIfDisconnected } from 'APP/Sagas/AppSessionSagas'
import { selectFeatures } from 'APP/Sagas/FeaturesSaga'
import Analytics from 'APP/Services/Analytics'
import { logger } from 'APP/Lib/Datadog'
import { LicenseVerificationStatus } from '@dialogue/services/dist/silkroad/model'
import { selectHistoryState } from 'APP/Store/History/selectors'
import { selectFamilyState } from 'APP/Store/Family/selectors'
import type {
  ArchiveEpisodePayload,
  CancelAppointmentPayload,
  FetchChannelsPayload,
  FetchEpisodeMembersPayload,
  FetchEpisodePayload,
  FetchPractitionerBioPayload,
  GoBackInEpisodePayload,
  LaunchEpisodeCommandPayload,
  RemoveUploadedFilePayload,
  RescheduleAppointmentPayload,
  RetryPostPayload,
  UpdateChannelLastViewedPayload,
} from 'APP/Store/History/types'
import type { TypedRoute } from 'APP/Nav/types'
import type { Profile } from '@dialogue/cms'
import type { RawPost } from 'APP/Services/Mattermost/types'
import {
  FlowType,
  type ConversationChannel,
  type ConversationEpisode,
  type ConversationMember,
  type GetFamilyAppointmentsResponse,
} from 'APP/Services/Silkroad/types'

import { fetchAllPostsForChannel, fetchNewPostsForChannel } from './fetchPosts'
import {
  broadcastMessageHandler,
  channelsConsistencyCheck,
  getConversationsPage,
  getLegacyEpisodes,
  handleNewMessage,
  loggedInState,
  mattermostLongPoll,
  mattermostWebsocket,
  refreshUnreadChannels,
  toLegacyEpisodes,
} from './utils'
import type { EnrichedEpisode } from '@dialogue/coredata'

const POSTS_FETCH_COUNT_FOR_CHANNEL_INIT = 25

type ConversationsPage = {
  mmChannels: ConversationChannel[]
  mmMembers: ConversationMember[]
  memberEpisodes: ConversationEpisode[]
}

/**
 * The full conversations page: episodes plus the Mattermost channels and memberships
 * they merge with.
 *
 * TODO: Remove the legacy branch once the flag is retired.
 */
export function* fetchConversations() {
  const { enableNewEpisodeFetch } = yield* select(selectFeatures)
  const accessToken = yield* select(selectAccessToken)
  const silkroad = Silkroad.create(accessToken)

  if (enableNewEpisodeFetch) {
    const { channels, members, episodes } = yield* getConversationsPage(silkroad)
    const page: ConversationsPage = {
      mmChannels: channels,
      mmMembers: members,
      memberEpisodes: episodes,
    }
    return page
  }

  const patient = yield* select(selectPatientState)
  const messagingTeamId = yield* select(selectMattermostTeamId)
  const mmClient = Mattermost.create(accessToken)

  const { mmChannels, mmMembers, memberEpisodes } = yield* all({
    mmChannels: call<
      Parameters<MattermostClient['getMyChannels']>,
      MattermostClient['getMyChannels']
    >(mmClient.getMyChannels, messagingTeamId),
    mmMembers: call<
      Parameters<MattermostClient['getMyChannelsMembers']>,
      MattermostClient['getMyChannelsMembers']
    >(mmClient.getMyChannelsMembers, messagingTeamId),
    memberEpisodes: getLegacyEpisodes(silkroad, patient.profile.id),
  })

  const page: ConversationsPage = {
    mmChannels,
    mmMembers,
    memberEpisodes: toLegacyEpisodes(memberEpisodes),
  }
  return page
}

export function* fetchChannels({ bootstrap = false }: FetchChannelsPayload = {}) {
  try {
    const loggedIn = yield* select(loggedInState)
    const { id } = yield* select(selectPatientProfile)
    if (loggedIn && id) {
      yield delay(200) // Debounce

      yield* call(channelsConsistencyCheck)

      const accessToken = yield* select(selectAccessToken)
      const { enableNewEpisodeFetch } = yield* select(selectFeatures)

      const mmClient = Mattermost.create(accessToken)
      const { mmChannels, mmMembers, memberEpisodes } = yield* fetchConversations()

      // The conversations endpoint returns the episodes alongside the channels, so a
      // bootstrap has no separate `fetchEpisodes` to make. They have to land before the
      // loop below, which only considers channels already in the store.
      if (bootstrap && enableNewEpisodeFetch) {
        yield put(PatientHistoryActions.fetchEpisodeSuccess(memberEpisodes))
      }

      const { channels, episodes } = yield* select(selectHistoryState)

      yield* spawn(setIsConnectedIfDisconnected)

      const updatedChannelIds = []

      const postFetches: Record<
        string,
        ReturnType<
          typeof call<
            Parameters<typeof fetchNewPostsRequestHelper>,
            typeof fetchNewPostsRequestHelper
          >
        >
      > = {}

      const newChannelInfo: Record<string, ConversationChannel> = {}
      const newMemberInfo: Record<string, ConversationMember> = {}
      const channelsToCheck = mmChannels.filter((c) => channels[c.id] && episodes[c.id])
      let i = channelsToCheck.length
      while (i > 0) {
        i--

        const channelInfo = channelsToCheck[i]
        const { id } = channelInfo
        const channel = channels[id]

        // Channels with errors or missing posts
        const forceFetchAllPosts =
          channel.error ||
          channel.missingData ||
          (channel.order && channel.order.length === 0 && !!channel.msgCount)

        // Channels with new posts
        // The msg count diff on the all channels info fetch lets us efficiently see if there's a new post on any channel
        const msgCountDiff = channelInfo.total_msg_count - channel.msgCount
        const hasPostsToFetch = forceFetchAllPosts || msgCountDiff
        if (hasPostsToFetch) {
          postFetches[id] = call<
            Parameters<typeof fetchNewPostsRequestHelper>,
            typeof fetchNewPostsRequestHelper
          >(fetchNewPostsRequestHelper, {
            channelId: id,
            // @ts-expect-error FIXME: this should be a boolean and sometimes isn't
            forceFetchAllPosts,
            // @ts-expect-error FIXME: this sometimes returns a boolean which is an invalid value type here
            limit: bootstrap && POSTS_FETCH_COUNT_FOR_CHANNEL_INIT,
            channel,
            mmClient,
          })
        }

        // Channels with new member info (used for last_viewed_at)
        let hasNewMemberInfo = false
        const matchingMember = mmMembers.find((member) => member.channel_id === id)
        if (matchingMember) {
          const hasLastViewedAtDiff = matchingMember.last_viewed_at !== channel.lastViewedAt
          if (hasLastViewedAtDiff) {
            hasNewMemberInfo = true
            newMemberInfo[id] = matchingMember
          }
        }

        // Channels with new channel info
        const hasNewChannelInfo = channelInfo.update_at !== channel.updateAt
        if (hasPostsToFetch || hasNewMemberInfo || hasNewChannelInfo) {
          newChannelInfo[id] = channelInfo
          updatedChannelIds.push(id)
        }
      }

      const fetchedPosts = yield* all(postFetches)

      const updatedChannelSets = updatedChannelIds.map((id) => ({
        channelId: id,
        postData: fetchedPosts[id],
        channelInfo: newChannelInfo[id],
        memberInfo: newMemberInfo[id],
      }))

      yield put(PatientHistoryActions.fetchChannelsSuccess(updatedChannelSets, memberEpisodes))
      yield* call(refreshUnreadChannels)

      if (!bootstrap) {
        // FIXME: this is not a post object, it's a post data object
        // Once fixed, narrow the args type for handleNewMessage
        const newPost = Object.values(fetchedPosts).find(
          (postData) => isValidPostData(postData) && postData.posts[postData.order[0]]
        )
        if (newPost) {
          yield* call(handleNewMessage, newPost)
        }
      }
    }
  } catch (error) {
    logger.debug('fetchChannels saga error', { bootstrap }, error)
    yield put(PatientHistoryActions.fetchChannelsFailure(error))
  }
}

export function* fetchEpisodes() {
  const { enableNewEpisodeFetch } = yield* select(selectFeatures)
  yield put(PatientHistoryActions.fetchEpisodeRequest())

  try {
    const accessToken = yield* select(selectAccessToken)
    const silkroad = Silkroad.create(accessToken)

    if (enableNewEpisodeFetch) {
      const { episodes } = yield* getConversationsPage(silkroad)
      yield put(PatientHistoryActions.fetchEpisodeSuccess(episodes))
      return
    }

    const patient = yield* select(selectPatientState)
    const memberEpisodes = yield* getLegacyEpisodes(silkroad, patient.profile.id)
    yield put(PatientHistoryActions.fetchEpisodeSuccess(toLegacyEpisodes(memberEpisodes)))
  } catch (err) {
    logger.debug('fetchEpisodes saga error', {}, err)
    yield put(PatientHistoryActions.fetchEpisodeFailure(err))
  }
}

export function* handleNewChannel(channelId: string) {
  const history = yield* select(selectHistoryState)
  const episode = history.episodes[channelId]
  const channel = history.channels[channelId]
  if (!episode || isChannelInconsistent(channel)) {
    yield* call(channelsConsistencyCheck)
    // TODO: Future optimization -- only fetch the required episode.
    yield* call(fetchEpisodes)
    yield* call(fetchAllPostsForChannel, { channelId })
  }
}

/**
 * Long-running saga that manages the necessary requests to Mattermost for chat messages (WebSocket + long-polling).
 * This waits for logout or long backgrounding to stop on its own.
 * To close all connections manually, dispatch `mattermostStop`.
 */
export function* mattermostStart() {
  logger.info('mattermostStart saga starting')
  if (yield* select(loggedInState)) {
    const bgMattermostWebsocketTask = yield* fork(mattermostWebsocket)
    const bgMattermostLongPollTask = yield* fork(mattermostLongPoll)

    const { backgrounded, logout, stop } = yield race({
      backgrounded: take(AppSessionTypes.SET_APP_BACKGROUNDED),
      logout: take(LoginTypes.LOGOUT),
      stop: take(PatientHistoryTypes.MATTERMOST_STOP),
    })

    logger.info('Stopping Mattermost connections...', {
      backgrounded: backgrounded !== undefined,
      logout: logout !== undefined,
      stop: stop !== undefined,
    })
    yield cancel(bgMattermostWebsocketTask)
    yield cancel(bgMattermostLongPollTask)
  }
}

export function* retryPost({ channelId, postId }: RetryPostPayload) {
  const accessToken = yield* select(selectAccessToken)
  const history = yield* select(selectHistoryState)
  const language = yield* select(selectPreferredLanguage)
  const adminAreaISOCode = yield* select(selectPatientRegion)

  const channel = history.channels[channelId]
  const postToRetry = channel.pendingPosts[postId]
  if (postToRetry) {
    const mmClient = Mattermost.create(accessToken)
    const deviceId = DeviceInfo.getUniqueId()
    let file_ids: string[] = []
    if (postToRetry.localFile) {
      try {
        const imageUploadResponse = yield* call(
          mmClient.uploadFile,
          channelId,
          buildInitialFilePayload(postToRetry)
        )
        file_ids = [imageUploadResponse.file_infos[0].id]
      } catch (err) {
        logger.error('Failed to upload file while retrying post', { channelId, postId })
        yield put(PatientHistoryActions.submitPostFailure(channelId, postId, err.message))
        return
      }
    }
    const postProps = postToRetry.props
    const flags = omit(postProps, ['localId', 'failed'])
    const response = yield* call<
      Parameters<MattermostClient['createUserPost']>,
      MattermostClient['createUserPost']
    >(
      mmClient.createUserPost,
      channelId,
      postToRetry.message,
      file_ids,
      deviceId,
      postId,
      postProps && postProps.answer,
      flags,
      undefined,
      language,
      adminAreaISOCode
    )
    if (response.ok && response.data) {
      yield put(PatientHistoryActions.submitPostSuccess(channelId, response.data))
    } else {
      yield put(PatientHistoryActions.submitPostFailure(channelId, postId, undefined))

      if (flags.retryCount && flags.retryCount >= 3) {
        yield put(PatientHistoryActions.setFailedPostError(channelId, true))
      }
    }
  }
}

export function* navigateToLink({ link, context }) {
  try {
    if (NestedNavHelper.getRouteNames().indexOf(link) !== -1) {
      const { link_type, ...linkContext } = context || {}
      switch (link_type) {
        case 'REPLACE':
          yield* call(NestedNavHelper.replace, link, linkContext)
          break
        case 'RESET':
          yield* call(NestedNavHelper.reset, {
            index: 0,
            routes: [{ name: link, params: linkContext }],
          })
          break
        case 'BACK_ACTION':
          yield* call(NestedNavHelper.pop)
          break
        case 'FOCUS':
          yield* call(NestedNavHelper.navigate, link, linkContext)
          break
        case 'PUSH':
        default:
          // BM provides episode_id in the linkContext for redirection,
          // but 'conversation' screen still uses channelId
          if (canRedirectToConversation(link, linkContext)) {
            linkContext.channelId = linkContext.episode_id
            // Navigating between conversations can trigger an unwanted redirection (goBack) if the previous channel is archived.
            // To prevent this, we set a temporary checkpoint here.
            // The archive handler (handleChannelArchiveEvent) checks for this ID
            // then skips the `goBack` call, ensuring smooth navigation between conversations.
            yield put(PatientHistoryActions.setInternalNavigationEpisodeId(linkContext.channelId))
            yield* call(NestedNavHelper.replace, link, linkContext)
          } else {
            yield* call(NestedNavHelper.navigate, link, linkContext)
          }
          break
      }
    } else if (yield* call(Linking.canOpenURL, link)) {
      yield* call(Linking.openURL, link)
    } else {
      throw Error(`Link cannot be open`, { cause: link })
    }
    yield put(PatientHistoryActions.navigateToLinkSuccess())
  } catch (err) {
    const error = Error('Navigate to external link failed', { cause: err })
    yield put(PatientHistoryActions.navigateToLinkError(error))
    // TODO: add some context to log once function args are typed
    logger.error('Failed to navigate to link', {}, err)
  }
}

export function* submitPost({ channelId, answer, fileObj, text, timeStamp, flags, payload }) {
  const accessToken = yield* select(selectAccessToken)
  const language = yield* select(selectPreferredLanguage)
  const adminAreaISOCode = yield* select(selectPatientRegion)

  const mmClient = Mattermost.create(accessToken)
  const deviceId = DeviceInfo.getUniqueId()

  let file_ids = []
  let message = answer ? answer.text : text
  let fileUploadResponse

  const extractedFileIds = extractFileIds(fileObj)

  if (extractedFileIds.length > 0) {
    file_ids = extractedFileIds
  } else if (fileObj && !Array.isArray(fileObj)) {
    try {
      yield put(PatientHistoryActions.fileUploadStarted(channelId))
      fileUploadResponse = yield* call(mmClient.uploadFile, channelId, fileObj)
      const uploadedFileInfo = fileUploadResponse?.file_infos?.[0]
      if (!uploadedFileInfo?.id) {
        throw new Error('Upload completed without file info id')
      }
      file_ids = [uploadedFileInfo.id]
    } catch (err) {
      logger.error('submitPost saga error', { endpoint: 'api/v4/files', channelId }, err)
      yield put(PatientHistoryActions.fileUploadFailed(channelId, err))
      yield put(PatientHistoryActions.submitPostFailure(channelId, timeStamp, err.message))
      return
    }
  }

  if (file_ids.length > 0) {
    message = 'Image Upload'
  }

  const localPostId = timeStamp

  try {
    if (flags?.selected_choice?.link) {
      yield* call(navigateToLink, {
        link: flags?.selected_choice?.link,
        context: flags?.selected_choice?.context,
      })
    }

    const response = yield* call<
      Parameters<MattermostClient['createUserPost']>,
      MattermostClient['createUserPost']
    >(
      mmClient.createUserPost,
      channelId,
      message,
      file_ids,
      deviceId,
      localPostId,
      answer,
      flags,
      payload,
      language,
      adminAreaISOCode
    )

    // @ts-expect-error FIXME: only dispatch submitPostSuccess if we got a success response
    yield put(PatientHistoryActions.submitPostSuccess(channelId, response.data))
    if (file_ids.length && fileUploadResponse?.file_infos?.[0]) {
      yield put(
        PatientHistoryActions.fileUploadCompleted(channelId, fileUploadResponse.file_infos[0])
      )
    }
  } catch (err) {
    yield put(PatientHistoryActions.submitPostFailure(channelId, localPostId, undefined))
    logger.error('Failed to submit post', { channelId }, err)
  }
}

export function* uploadFile({ channelId, file }) {
  if (!channelId || !file) return

  try {
    yield put(PatientHistoryActions.fileUploadStarted(channelId))
    const accessToken = yield* select(selectAccessToken)
    const mmClient = Mattermost.create(accessToken)
    const response = yield* call(mmClient.uploadFile, channelId, file)
    const uploadedFileInfo = response?.file_infos?.[0]
    if (!uploadedFileInfo?.id) {
      throw new Error('Upload completed without file info id')
    }
    yield put(PatientHistoryActions.fileUploadCompleted(channelId, uploadedFileInfo))
  } catch (err) {
    logger.error('Failed to upload file', { channelId })
    yield put(PatientHistoryActions.fileUploadFailed(channelId, err))
  }
}

export function* removeUploadedFile({ channelId, fileId }: RemoveUploadedFilePayload) {
  if (!channelId) {
    return
  }
  yield put(PatientHistoryActions.removeUploadedFileSuccess(channelId))
  const label = fileId ?? 'no_file_id'
  Analytics.trackStructuredEvent('Chat Action', 'File upload removed', label, 'success', 1)
}

export function* updateChannelLastViewed({ channelId }: UpdateChannelLastViewedPayload) {
  if (channelId) {
    try {
      const accessToken = yield* select(selectAccessToken)
      const messagingTeamId = yield* select(selectMattermostTeamId)

      if (messagingTeamId && channelId) {
        const mmClient = Mattermost.create(accessToken)
        yield* call(mmClient.viewMyChannel, channelId)
      }
      yield* spawn(refreshUnreadChannels)
    } catch (err) {
      logger.warn('updateChannelLastViewed saga error', { channelId }, err)
    }
  }
}

export function* resetTypingEventDataAfterDelay() {
  yield delay(2000)
  yield put(PatientHistoryActions.setTypingEventData(null))
}

export function* receiveEvent(action) {
  const eventType = action.event.event
  const channelId: string | undefined = action.event.broadcast?.channel_id
  if (channelId) {
    yield* call(handleNewChannel, channelId)
    const post = JSON.parse(action?.event?.data?.post || '{}')
    yield* spawn(broadcastMessageHandler, channelId, post)
  }
  switch (eventType) {
    case 'posted':
      try {
        if (!channelId) break // channelId is required for processing the post
        yield put(PatientHistoryActions.setTypingEventData(null))

        const post = JSON.parse(action.event.data.post) as RawPost

        if (!isSelfPost(post)) {
          const accessToken = yield* select(selectAccessToken)
          const postData = yield* call<
            Parameters<typeof resolveExtraProps>,
            typeof resolveExtraProps
          >(
            resolveExtraProps,
            {
              order: [post.id],
              posts: {
                [post.id]: post,
              },
            },
            accessToken
          )
          yield put(PatientHistoryActions.receivePosts(channelId, postData, undefined))

          if (isEncounterPost(post)) {
            yield put(PatientActions.patientProfileFetchRequest())
          }

          yield* call(handleNewMessage, post)

          // Another instance of Member App requested to go back on this post id.
          if (post.props?.type === 'action_back' && post.props?.undone_post_id) {
            yield put(PatientHistoryActions.setPostAsUndone(post.props.undone_post_id))
          }
        }

        if (post.type === 'system_header_change') {
          const messageComponents = post.message && post.message.split('to: ')
          const header = messageComponents && messageComponents.length === 2 && messageComponents[1]
          yield put(PatientHistoryActions.updateChannelHeader(channelId, header))
        }

        // Update the episode, each time when assigne changed and only when the episode is open.
        const history = yield* select(selectHistoryState)
        if (post.type === 'system_add_remove' && history.episode.id) {
          yield put(PatientHistoryActions.fetchOneEpisodeRequest(history.episode.id))
        }

        const { dialogue_type } = post.props || {}
        if (dialogue_type === 'episode_state_changed') {
          yield put(PatientHistoryActions.updateChannelState(channelId, post.props.state))
        }

        if (dialogue_type === 'episode_message_retracted') {
          const postIdToHide = post.props.deeplink && post.props.deeplink.message_id
          yield put(PatientHistoryActions.removePosts(channelId, [postIdToHide]))
        }
      } catch (e) {
        logger.debug('Error processing posted message event', { channelId }, e)
      }
      break
    case 'typing':
      yield put(PatientHistoryActions.setTypingEventData(action.event))
      yield put(PatientHistoryActions.resetTypingEventData())
      break
    default:
      break
  }
}

export function* getOrCreateIcbtEpisode() {
  const accessToken = yield* select(selectAccessToken)
  const profile = yield* select(selectPatientProfile)
  const er = new EmergencyRoom(accessToken, `${Config.EMERGENCY_ROOM_DOMAIN}/v1`)

  const { eligibleServices } = profile

  yield* call(Nav.navigate, 'newConversation')

  try {
    const hasIcbtGuided = Object.prototype.hasOwnProperty.call(eligibleServices, 'icbt_guided')

    if (!hasIcbtGuided) {
      throw new Error('Tried to create an iCBT guided episode without access to the service.')
    }

    const locationPermission = yield checkLocationService()
    const { data, status } = yield* call(er.getOrCreateIcbtEpisode)
    const episode = data as EnrichedEpisode
    // istanbul ignore next
    const command =
      Config.EPISODE_CREATION_COMMANDS.icbtCoach || Config.SUPPORTED_CHAT_BOT.default_intent_command
    const flowType = FlowType.icbt_coach
    if (status === 201 && command) {
      const silkroad = Silkroad.create(accessToken)
      yield* call(silkroad.startFlow, { episodeId: episode.id, flowType })
      yield put(
        PatientHistoryActions.createEpisodeSuccess(episode, command, locationPermission, flowType)
      )
    }
    yield* call(Nav.setParams, { channelId: episode.id })
    yield* call(fetchNewPostsForChannel, { channelId: episode.id })
  } catch (err) {
    yield put(PatientHistoryActions.createEpisodeFailure(err))
    // TODO: add some context to log once function args are typed
    logger.debug('getOrCreateIcbtEpisode saga error', {}, err)
  }
}

export function* fetchEpisode({ channelId }: FetchEpisodePayload) {
  const accessToken = yield* select(selectAccessToken)
  const coredataClient = CoreData.create(accessToken)

  try {
    const response = yield* call(coredataClient.getEpisode, channelId)
    yield put(PatientHistoryActions.fetchOneEpisodeSuccess(response))
  } catch (error) {
    logger.debug('fetchEpisode saga error', { channelId }, error)
  }
}

export function* fetchEpisodeMembers({ channelId }: FetchEpisodeMembersPayload) {
  if (!channelId) return
  const accessToken = yield* select(selectAccessToken)
  const mmClient = Mattermost.create(accessToken)

  try {
    const response = yield* call(mmClient.getMyChannelMembers, channelId)
    yield put(PatientHistoryActions.fetchEpisodeMembersSuccess(response))
  } catch (error) {
    logger.debug('fetchEpisodeMembers saga error', { channelId }, error)
  }
}

export function* fetchPractitioners() {
  if (!Config.USHER_DOMAIN) return
  const login = yield* select(selectLoginState)
  const usher = Usher.create(login)
  yield put(PatientHistoryActions.fetchPractitionersRequest())
  try {
    const data = yield* call(usher.getPractitionersInfo)
    yield put(PatientHistoryActions.fetchPractitionersSuccess(data))
  } catch (error) {
    logger.debug('fetchPractitioners saga error', {}, error)
    yield put(PatientHistoryActions.fetchPractitionersFailure(error))
  }
}

export function* launchEpisodeCommand({
  command,
  flowType,
  healthIssueTypeId,
}: LaunchEpisodeCommandPayload) {
  const accessToken = yield* select(selectAccessToken)
  const profile = yield* select(selectPatientProfile)

  yield* call(Nav.navigate, 'newConversation')
  try {
    const locationPermission = yield checkLocationService()

    const silkroad = Silkroad.create(accessToken)
    let response = yield* call<
      Parameters<SilkroadClient['createEpisode']>,
      SilkroadClient['createEpisode']
    >(silkroad.createEpisode, profile.id, {
      health_issue_type_id: healthIssueTypeId,
    })

    yield* call(
      silkroad.sendCommand,
      response.id,
      addCommandSlots(command, { fullscreen_enabled: true })
    )

    yield put(
      PatientHistoryActions.createEpisodeSuccess(response, command, locationPermission, flowType)
    )
    yield* call(Nav.setParams, { channelId: response.id })
    yield* call(fetchNewPostsForChannel, { channelId: response.id })
  } catch (err) {
    yield put(PatientHistoryActions.createEpisodeFailure(err))
    // TODO: add some context to log once function args are typed
    logger.debug('launchEpisodeCommand saga error', {}, err)
  }
}

export function* getOrCreateWbiEpisode() {
  try {
    const command = Config.EPISODE_CREATION_COMMANDS.wbiAssessment
    const flowType = FlowType.wbi_assessment
    if (!command) {
      throw Error('Config.EPISODE_CREATION_COMMANDS.wbiAssessment required')
    }

    const accessToken = yield* select(selectAccessToken)
    const er = new EmergencyRoom(accessToken, `${Config.EMERGENCY_ROOM_DOMAIN}/v1`)

    const hasLocationPermission = yield checkLocationService()

    const { data: episode, status } = yield* call(er.getOrCreateWbiEpisode)
    if (![200, 201].includes(status)) {
      throw Error('get or create wbi episode failed', { cause: [status, episode] })
    }

    const silkroad = Silkroad.create(accessToken)
    yield* call(silkroad.startFlow, { episodeId: episode.id, flowType })

    yield put(
      PatientHistoryActions.createEpisodeSuccess(episode, command, hasLocationPermission, flowType)
    )

    yield* call(fetchNewPostsForChannel, { channelId: episode.id })
    yield put(PatientHistoryActions.resetChannels([episode.id]))
    yield* call(Nav.navigate, 'newConversation', {
      exitAlert: i18n.t('WellBeingIndexScreen.intakeExitAlert', { returnObjects: true }),
      channelId: episode.id,
    })
  } catch (err) {
    yield put(PatientHistoryActions.createEpisodeFailure(err))
    // TODO: add some context to log once function args are typed
    logger.error('Failed to get or create WBI episode', {}, err)
    Toast.show({ text1: i18n.t(`WellBeingIndexScreen.takeAssessmentError`) })
  }
}

export function* fetchAppointments() {
  yield put(PatientHistoryActions.fetchAppointmentsRequest())

  const accessToken = yield* select(selectAccessToken)
  const patient = yield* select(selectPatientProfile)
  const family = yield* select(selectFamilyState)
  const language = yield* select(selectPreferredLanguage)
  const adminAreaISOCode = yield* select(selectPatientRegion)

  try {
    const silkroad = Silkroad.create(accessToken)
    // @ts-expect-error @dialogue/silkroad models are source of truth, @dialogue/services is outdated
    const response: GetFamilyAppointmentsResponse = yield* call<
      Parameters<(typeof silkroad)['getFamilyAppointments']>,
      (typeof silkroad)['getFamilyAppointments']
    >(
      silkroad.getFamilyAppointments,
      // @ts-expect-error FIXME: handle falsy profile ID
      patient.id,
      {
        include_licenses: true,
        language,
        location: adminAreaISOCode,
        license_verification_status: LicenseVerificationStatus.VERIFIED,
      }
    )
    const data = response?.data || []
    const appointments = normalizeAppointments(data, family)

    for (const group of data) {
      for (const practitioner of group?.practitioners ?? []) {
        if (practitioner?.id) {
          yield put(providerBookingActions.getProviderSuccess(practitioner))
        }
      }
    }

    const bios = yield* all<Generator<any, null | Profile | undefined, any>>(
      appointments.upcoming.map((appointment) =>
        (function* () {
          const practitionerId = appointment?.practitioner?.id
          if (!practitionerId) {
            return null
          }
          try {
            return yield* call<Parameters<typeof getBio>, typeof getBio>(getBio, practitionerId)
          } catch (_e) {
            return null
          }
        })()
      )
    )

    appointments.upcoming = appointments.upcoming.map((appointment, i) => {
      const bio = bios[i]
      return {
        ...appointment,
        practitioner: {
          ...appointment?.practitioner,
          job_title: bio?.job_title,
          picture: bio?.profile_picture?.url,
        },
      }
    })

    yield put(PatientHistoryActions.fetchAppointmentsSuccess(appointments))
  } catch (error) {
    logger.debug('fetchAppointments saga error', {}, error)
    yield put(PatientHistoryActions.fetchAppointmentsFailure(error))
  }
}

/**
 * Retrieves the current navigation routes from Nav state.
 *
 * @returns {Array<Object>} An array of route objects, or an empty array if none.
 */
export function getCurrentRoutes() {
  if (Nav.isReady()) {
    return Nav?.getState()?.routes || []
  }
  return []
}

/**
 * Creates a navigation state for resetting to a conversation screen
 *
 * - Removes 'upcomingAppointment', 'conversation' or 'newConversation'screens from navigation history
 * - Adds a new conversation screen at the end of the stack
 * - Sets the active route index to point to this conversation screen
 *
 * This ensures the conversation screen is always the last screen in the navigation stack,
 * while preserving the rest of the navigation history.
 *
 * @param {string} channelId - Episode/channel ID to pass to the conversation screen as params
 * @param {Array<Object>} routes - The current array of navigation routes
 * @returns {Object} The navigation state object with the updated routes and active route
 */
export function buildConversationState(channelId: string, routes: TypedRoute[]) {
  const filteredRoutes = routes.filter(
    (route) =>
      route?.name !== 'upcomingAppointment' &&
      route?.name !== 'conversation' &&
      route?.name !== 'newConversation'
  )

  // Add the conversation screen at the end
  filteredRoutes.push({
    name: 'conversation',
    params: { channelId },
  })

  return {
    routes: filteredRoutes,
    index: filteredRoutes.length - 1,
  }
}

/**
 * Composes a navigation state for resetting to a conversation screen.
 *
 * @param channelId - Episode/channel ID for conversation params.
 * @returns Navigation state with routes array and active route index.
 */
export function buildConversationNavigationState(channelId: string) {
  const routes = getCurrentRoutes() as TypedRoute[]
  return buildConversationState(channelId, routes)
}

export function* rescheduleAppointment({ appointment }: RescheduleAppointmentPayload) {
  const accessToken = yield* select(selectAccessToken)

  try {
    const silkroad = Silkroad.create(accessToken)
    yield* call(silkroad.rescheduleAppointment, appointment.id)

    // @ts-expect-error FIXME: episode_id is nullable on appointments, for legacy reasons
    const navigationState = buildConversationNavigationState(appointment.episode_id)

    yield delay(600)
    yield* call(Nav.reset, navigationState)

    yield put(PatientHistoryActions.rescheduleAppointmentSuccess())
  } catch (err) {
    // TODO: add some context to log once function args are typed
    logger.debug('rescheduleAppointment saga error', {}, err)
    yield put(PatientHistoryActions.rescheduleAppointmentFailure(err))
  }
}

export function* cancelAppointmentFromCalendar({ appointment }) {
  try {
    yield* call(updateAppointment, {
      appointment_id: appointment.id,
      appointment: {
        status: 'cancelled',
      },
    })
    yield put(PatientHistoryActions.cancelAppointmentFromCalendarSuccess())
  } catch (err) {
    // TODO: add some context to log once function args are typed
    logger.debug('cancelAppointmentFromCalendar saga error', {}, err)
    yield put(PatientHistoryActions.cancelAppointmentFromCalendarFailure(err))
    yield delay(600)
    Toast.show({
      text1: i18n.t('AppointmentBooking.cancelAppointmentSheet.error'),
      type: 'error',
    })
  }
}

export function* cancelAppointment({ appointment }: CancelAppointmentPayload) {
  const accessToken = yield* select(selectAccessToken)

  const navigationState = buildConversationNavigationState(appointment.episode_id)

  try {
    const silkroad = Silkroad.create(accessToken)
    const { appointment_is_cancelled } = yield* call(
      silkroad.cancelAppointment,
      appointment.tk_appointment.id
    )

    // Handle scenario where the appointment was not cancelled but the error was successfully handled
    // We navigate to the episode because the member is placed in a queue for assistance
    if (!appointment_is_cancelled) {
      yield* call(Nav.reset, navigationState)
      throw Error('Failed to cancel appointment')
    }

    yield put(PatientHistoryActions.cancelAppointmentSuccess())

    // We should refresh appointments after cancellation since other components may depend on it
    yield put(PatientHistoryActions.refreshAppointmentsRequest())

    // This delay is workaround fix to allow the Modal to properly dismount before navigating, otherwise it crashes on iOS
    // source: https://github.com/react-navigation/react-navigation/issues/11270
    yield delay(600)

    yield* call(Nav.reset, navigationState)

    sprigClient.track('CANCEL_APPOINTMENT_SUCCESS', {
      episode_id: appointment.episode_id,
    })
  } catch (err) {
    // TODO: add some context to log once function args are typed
    logger.debug('cancelAppointment saga error', {}, err)
    yield put(PatientHistoryActions.cancelAppointmentFailure(err))
  }
}

export function* fetchPractitionerBio({ userAppId, profileId }: FetchPractitionerBioPayload) {
  yield put(PatientHistoryActions.fetchPractitionerBioRequest())
  try {
    const practitionerBio = yield* call(getBio, userAppId, undefined, profileId)
    yield put(PatientHistoryActions.fetchPractitionerBioSuccess(camelizeKeys(practitionerBio)))
  } catch (error) {
    // TODO: add some context to log once function args are typed
    logger.debug('fetchPractitionerBio saga error', {}, error)
    yield put(PatientHistoryActions.fetchPractitionerBioFailure(error))
  }
}

export function* archiveEpisode({ channelId, sprigSurveyData }: ArchiveEpisodePayload) {
  const accessToken = yield* select(selectAccessToken)
  const coredataClient = CoreData.create(accessToken)
  try {
    yield* call(coredataClient.updateEpisodeState, channelId, 'archived')
    yield* call(fetchEpisodes)
    yield put(PatientHistoryActions.archiveEpisodeSuccess())

    const { currentChannelId } = yield* select(selectHistoryState)
    const onConversationScene = CONVERSATION_ROUTES.indexOf(Nav.getCurrentRoute()?.name) !== -1

    if (onConversationScene && currentChannelId === channelId) {
      yield* call(Nav.goBack)
      yield delay(500)
      yield* call(showToast, { title: i18n.t('Intake.toast.success'), type: 'success' })
    }

    if (sprigSurveyData) {
      sprigClient.track(sprigSurveyData.trackName, sprigSurveyData.eventProperties)
    }
  } catch (error) {
    yield put(PatientHistoryActions.archiveEpisodeError(error))
    logger.debug('archiveEpisode saga error', { channelId }, error)
    yield* call(Nav.goBack)
    yield delay(500)
    Toast.show({ text1: i18n.t('Intake.toast.error'), type: 'error' })
  }
}

export function* goBackInEpisode({ channelId, postId }: GoBackInEpisodePayload) {
  try {
    if (channelId) {
      const accessToken = yield* select(selectAccessToken)
      const language = yield* select(selectPreferredLanguage)
      const adminAreaISOCode = yield* select(selectPatientRegion)
      const mmClient = Mattermost.create(accessToken)
      const deviceId = DeviceInfo.getUniqueId()

      const response = yield* call(
        mmClient.createUserPost,
        channelId,
        i18n.t('Intake.backButton.chatMessage'),
        [],
        deviceId,
        Date.now(),
        null,
        {
          type: 'action_back',
          version: '1',
          provider_app_visible: true,
          member_app_visible: true,
          undone_post_id: postId,
        },
        undefined,
        language,
        adminAreaISOCode
      )

      if (response.ok) {
        yield put(PatientHistoryActions.goBackInEpisodeSuccess(channelId, postId))
      } else {
        throw Error('goBackInEpisode createUserPost failure', {
          cause: { status: response.status, channelId, postId },
        })
      }
    }
  } catch (error) {
    yield put(PatientHistoryActions.goBackInEpisodeError(channelId, postId, error))
    logger.debug('goBackInEpisode saga error', { channelId, postId }, error)
  }
}
